import { AlertTriangleIcon, XCircleIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { isModelIssue, type PredicateIssue, type PredicateIssueType } from '@/shared/api';
import { cn } from '@/shared/lib';

type Props = {
  issues: PredicateIssue[];
  /** Условия по записи нет: тогда и отказ модели — проблема, из-за которой условия не получилось */
  isConditionMissing?: boolean;
  className?: string;
};

/** Ключи названий видов проблем в пространстве `common` */
const ISSUE_TITLE_KEYS = {
  SyntaxError: 'predicateIssues.syntaxError',
  UnknownAttribute: 'predicateIssues.unknownAttribute',
  DeletedAttribute: 'predicateIssues.deletedAttribute',
  InvalidOperator: 'predicateIssues.invalidOperator',
  TypeMismatch: 'predicateIssues.typeMismatch',
  DeletedValue: 'predicateIssues.deletedValue',
  ConvertationError: 'predicateIssues.convertationError',
} as const satisfies Record<PredicateIssueType, string>;

/**
 * Перечень проблем условия: каждая строка — вид проблемы, атрибут, к которому она
 * относится, и сообщение сервера. Отказ языковой модели отмечен мягче, пока условие
 * всё-таки построено.
 */
export const PredicateIssues: FC<Props> = ({ issues, isConditionMissing = false, className }) => {
  const { t } = useTranslation('common');

  if (!issues.length) {
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {issues.map((issue, index) => {
        // Проблема записи означает, что условия нет; отказ модели отмечен мягче, только
        // когда условие всё же построено — иначе он такая же причина его отсутствия
        const isModel = isModelIssue(issue) && !isConditionMissing;
        const Icon = isModel ? AlertTriangleIcon : XCircleIcon;
        const titleKey = ISSUE_TITLE_KEYS[issue.type];

        return (
          <div
            key={`${issue.type}-${issue.attributeCode ?? ''}-${index}`}
            className={cn(
              'flex gap-2 p-2 rounded-md text-sm',
              isModel ? 'bg-warning text-warning-foreground' : 'bg-destructive/10 text-destructive',
            )}
          >
            <Icon className="size-4 shrink-0 mt-0.5" />
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="font-medium">
                {titleKey ? t(titleKey) : issue.type}
                {issue.attributeCode ? ` · ${issue.attributeCode}` : ''}
              </span>
              <span className="break-words">{issue.message}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
